import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy, mapToCanActivate, mapToCanDeactivate } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { PortalAdminGuard } from '@core/guards';
import { MainComponent } from '@feature/main/main.component';
import { AccessPageGuard } from '@mynexus/mynexus-ui-lib';
import { HomeComponent } from '@pages/home/home.component';
import { MicroAppComponent } from '@shared/component/micro-app/micro-app.component';
import { LineOfBusinessResolver } from '@core/resolvers/line-of-business.resolver';
import DMEPOSRoutes from '@products/dmepos/routes';
import PACRoutes from '@products/pacim/routes';
import HHRoutes from '@products/hh/routes';
import CoreRoutes from '@products/core/routes';
import { NotesGuard } from './core';
import { CarelonTitleStrategy } from '@core/service/carelon-title.strategy';

const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    resolve: { lineOfBusiness: LineOfBusinessResolver },
    children: [
      {
        path: 'home',
        component: HomeComponent,
        title: 'Home',
        canActivate: mapToCanActivate([MsalGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: ':tenantId/member/:subscriberNumber',
        loadChildren: () => import('@feature/member-detail/member-detail.module')
          .then((m) => m.MemberDetailModule),
        title: 'Member Details',
        data: {
          showNotesPanel: true,
          permissions: ['MemberPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'activity-center',
        loadComponent: () => import('@pages/activity-center/activity-center.component').then((m) => m.ActivityCenterComponent),
        title: 'Activity Center',
        data: {
          permissions: ['ActivityCenterPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'claims',
        loadChildren: () =>
          import('@feature/claim-list/claim-list.module').then(
            (m) => m.ClaimListModule
          ),
        title: 'Claims',
        canActivate: mapToCanActivate([MsalGuard]),
      },
      {
        path: 'claims-search-by-details',
        loadChildren: () => import('@feature/claims-search-list/claims-search-list.module')
          .then((m) => m.ClaimsSearchListModule),
        title: 'Claims Search By Details',
        canActivate: mapToCanActivate([MsalGuard]),
      },
      {
        path: ':tenantId/AuthorizationsViewList',
        loadChildren: () => import('@feature/authorizations-history/authorizations-history.module')
          .then((m) => m.AuthorizationsHistoryModule),
        title: 'Authorization History',
        data: {
          permissions: ['AuthorizationHistoryPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'FaxHistory',
        loadChildren: () => import('@feature/documents-history/document-history.module')
          .then((m) => m.DocumentHistoryModule),
        title: 'Fax History',
        data: {
          permissions: ['FaxHistoryPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'advanced-search',
        loadChildren: () => import('@feature/advanced-search/advanced-search.module')
          .then((m) => m.AdvancedSearchModule),
        title: 'Advanced Search',
        data: {
          permissions: [
            'AdvancedSearchMember.Read',
            'AdvancedSearchServiceProvider.Read',
            'AdvancedSearchOrderingFollowingProvider.Read',
          ],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'AdminPanel',
        loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
        title: 'Admin',
        data: {
          permissions: ['AdminPage.UI', 'LetterMaps.Read'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
      },
      {
        path: 'daily-dashboard',
        loadChildren: () => import('@feature/daily-dashboard').then((m) => m.DailyDashboardModule),
        title: 'Daily Dashboard',
        data: { permissions: [
          'HHDailyDashboard.Read',
          'PACMADailyDashboard.Read',
          'PACCOMDailyDashboard.Read',
          'DMEPOSMedicareDailyDashboard.Read',
          'DMEPOSMedicaidDailyDashboard.Read',
        ] },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
      },
      {
        path: 'InboundFaxes',
        loadChildren: () => import('@feature/inbound-fax/inbound-fax.module')
          .then((m) => m.InboundFaxModule),
        title: 'Inbound Fax',
        data: {
          permissions: ['InboundFax.UI.Read'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'notifications',
        loadComponent: () => import('@pages/notifications/notifications.component').then(m => m.NotificationsPageComponent),
        title: 'Notifications',
        canActivate: mapToCanActivate([MsalGuard]),
      },
      ...DMEPOSRoutes,
      ...PACRoutes,
      ...HHRoutes,
      ...CoreRoutes,
      {
        path: '**',
        component: MicroAppComponent,
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    component: MicroAppComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    PortalAdminGuard,
    { provide: TitleStrategy, useClass: CarelonTitleStrategy },
  ],
})
export class AppRoutingModule { }
